import { decodeJWT } from "../library/utilization/AuthorizationUtilization";
// The client-side state of an impersonation, receive-side.
//
// A trimmed copy of admin-web's module: app-web is only ever the TARGET origin of a learner impersonation,
// which is a different origin from admin-web — so the admin's own session was never touched and there is
// nothing to stash or restore. The actor-snapshot half of admin-web's module has no meaning here and is
// deliberately absent rather than carried along unused.
//
// Every key is namespaced under `impersonation.` so `clearImpersonationState` can never reach a session key,
// and the app's own logout can never orphan a banner over a logged-out screen.

const PREFIX = "impersonation.";

const KEY = {
  active: `${PREFIX}active`,
  targetUserProfileId: `${PREFIX}targetUserProfileId`,
  targetLabel: `${PREFIX}targetLabel`,
  targetRole: `${PREFIX}targetRole`,
  impersonationSessionId: `${PREFIX}impersonationSessionId`,
  startedAt: `${PREFIX}startedAt`,
  destination: `${PREFIX}destination`,
};

export type ImpersonationState = {
  active: boolean;
  /**
   * The profile the session is acting through NOW: empty right after a user-level redeem (the token is
   * pre-profile until the picker), then rewritten by {@link updateImpersonationTarget} on every pick.
   */
  targetUserProfileId: string | null;
  targetLabel: string | null;
  targetRole: string | null;
  impersonationSessionId: string | null;
  startedAt: number | null;
  /** `APP_WEB` for every session this app receives (admin-web's destination modal), or empty on a legacy one. */
  destination: string | null;
};

/** Twelve hours. Past this the session's tab is certainly gone; do not keep showing a banner for it. */
export const IMPERSONATION_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * Fired on THIS tab whenever the impersonation flags change.
 *
 * <p>The `storage` event is not enough on its own: by specification it fires only in OTHER tabs, never in
 * the one that performed the write. The tab that redeems the handoff code is exactly that tab, and it ends
 * on an SPA navigation rather than a reload — so the banner, whose state was captured when it mounted,
 * never learned an impersonation had begun and silently never appeared. The non-dismissible safety band
 * was missing precisely where the design depends on it.
 */
/**
 * Whether a token itself says it is an impersonation — i.e. carries the `act` claim management stamps on a
 * redeemed handoff (see catchup-security `JwtUtilization.generateTokenFromValidUserDetails`).
 *
 * <p>This, not the storage flag, is the authoritative client-side answer. The flag is a plain boolean with a
 * 12-hour horizon that only three code paths clear, so an abandoned impersonation left it set for the NEXT
 * person to sign in on that browser — who then took the impersonation branch in the profile picker, kept an
 * unscoped token, and saw their own profile list filtered down to the previous target. Asking the live token
 * cannot go stale, because it IS the session.
 */
export const isImpersonatedToken = (token?: string | null): boolean => {
  if (!token) return false;
  try {
    const decoded: any = decodeJWT(token);
    return Boolean(decoded && typeof decoded === "object" && decoded.act);
  } catch {
    return false;
  }
};

/** The stored access token's own answer to "am I an impersonation?". */
export const isImpersonatingBySession = (storage?: Storage): boolean =>
  isImpersonatedToken(store(storage).getItem("accessToken"));

export const IMPERSONATION_STATE_EVENT = "impersonationStateChanged";

/** Same-tab notification. No-ops outside a browser so the pure-unit tests stay pure. */
const announceStateChange = (): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(IMPERSONATION_STATE_EVENT));
};

const store = (storage?: Storage): Storage => storage ?? window.localStorage;

export const isImpersonationActive = (storage?: Storage): boolean =>
  store(storage).getItem(KEY.active) === "true";

export const readImpersonationState = (
  storage?: Storage,
): ImpersonationState => {
  const s = store(storage);
  const startedAt = s.getItem(KEY.startedAt);
  return {
    active: s.getItem(KEY.active) === "true",
    targetUserProfileId: s.getItem(KEY.targetUserProfileId),
    targetLabel: s.getItem(KEY.targetLabel),
    targetRole: s.getItem(KEY.targetRole),
    impersonationSessionId: s.getItem(KEY.impersonationSessionId),
    startedAt: startedAt ? Number(startedAt) : null,
    destination: s.getItem(KEY.destination),
  };
};

/** Refuses while one is already active, so a second redeem cannot silently retarget the banner. */
export const beginImpersonation = (
  target: {
    userProfileId?: string | number | null;
    label?: string | null;
    role?: string | null;
    impersonationSessionId?: string | number | null;
    destination?: string | null;
  },
  nowMs: number,
  storage?: Storage,
): boolean => {
  const s = store(storage);
  if (isImpersonationActive(s)) return false;
  s.setItem(KEY.active, "true");
  s.setItem(KEY.targetUserProfileId, String(target.userProfileId ?? ""));
  s.setItem(KEY.targetLabel, target.label ?? "");
  s.setItem(KEY.targetRole, target.role ?? "");
  s.setItem(
    KEY.impersonationSessionId,
    String(target.impersonationSessionId ?? ""),
  );
  s.setItem(KEY.startedAt, String(nowMs));
  s.setItem(KEY.destination, target.destination ?? "");
  announceStateChange();
  return true;
};

/**
 * Records the profile an ACTIVE impersonation is now acting through — the picker's first choice after a
 * user-level redeem, and every "Profil değiştir" after it — so the banner names the right role.
 *
 * <p>Refuses (returns false) when no impersonation is active: this must never be what starts one.
 */
export const updateImpersonationTarget = (
  target: { userProfileId?: string | number | null; role?: string | null },
  storage?: Storage,
): boolean => {
  const s = store(storage);
  if (!isImpersonationActive(s)) return false;
  s.setItem(KEY.targetUserProfileId, String(target.userProfileId ?? ""));
  s.setItem(KEY.targetRole, target.role ?? "");
  announceStateChange();
  return true;
};

/**
 * Clears every impersonation key.
 *
 * <p>Enumerates the namespace rather than removing a remembered list, so a key added later cannot be
 * orphaned into a banner that outlives its session.
 */
export const clearImpersonationState = (storage?: Storage): void => {
  const s = store(storage);
  const doomed: string[] = [];
  for (let index = 0; index < s.length; index += 1) {
    const key = s.key(index);
    if (key && key.startsWith(PREFIX)) doomed.push(key);
  }
  doomed.forEach((key) => s.removeItem(key));
  announceStateChange();
};

/** True when the flags are old enough that the session behind them is certainly over. */
export const isImpersonationStale = (
  nowMs: number,
  startedAt: number | null,
  maxAgeMs: number = IMPERSONATION_MAX_AGE_MS,
): boolean => {
  if (startedAt === null || Number.isNaN(startedAt)) return true;
  return nowMs - startedAt > maxAgeMs;
};
